import { addDays, addWeeks, endOfDay, formatDistanceStrict, isAfter, isValid, subDays } from 'date-fns';
import { compact } from 'lodash';
import * as Yup from 'yup';

export function rideSchema(isSeries, nextAvailableDate, currentDate, startDateDisabled) {
  const MAXIMUM_SERIES_LENGTH_IN_WEEKS = 52.14;

  return Yup.object().shape({
    time: Yup.string().required('Anchor Time is Required'),
    waypointsAttributes: Yup.array()
      .min(1)
      .of(
        Yup.object().shape({
          location: Yup.object()
            .shape({
              address: Yup.string().required('Required Field'),
              placeId: Yup.string().required('Location is Required'),
              id: Yup.string().required('Required Field'),
            })
            .required('Location is Required'),
          anchor: Yup.boolean().required('Required Field'),
          passengerIds: Yup.array().min(1, 'At least one Rider is required').required('Riders are required'),
        })
      ),
    anchorTime: Yup.lazy(() => {
      if (!isSeries) {
        const minDate = new Date(nextAvailableDate.setHours(0, 0, 0, 0));

        return Yup.date()
          .min(minDate, 'Ride date is not a valid date')
          .nullable()
          .required('Ride Date is Required')
          .typeError('Ride date is not a valid date');
      }

      return Yup.mixed().notRequired();
    }),
    tripTemplateAttributes: Yup.object().shape({
      repeats: Yup.lazy(() => {
        if (isSeries) {
          return Yup.bool().when(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'], {
            is: (monday, tuesday, wednesday, thursday, friday, saturday, sunday) => {
              return compact([monday, tuesday, wednesday, thursday, friday, saturday, sunday]).length === 0;
            },
            then: Yup.bool().required('At least one repeating day must be selected'),
          });
        }

        return Yup.bool().notRequired();
      }),
      monday: Yup.bool(),
      tuesday: Yup.bool(),
      wednesday: Yup.bool(),
      thursday: Yup.bool(),
      friday: Yup.bool(),
      saturday: Yup.bool(),
      sunday: Yup.bool(),
      startDate: Yup.lazy(() => {
        if (isSeries) {
          return Yup.mixed()
            .required('Start Date is Required')
            .when('endDate', (endDate) => {
              if (startDateDisabled) {
                return Yup.date();
              }
              const minDate = new Date(nextAvailableDate.setHours(0, 0, 0, 0));
              const maxDate = subDays(endDate, 1);

              if (isValid(endDate)) {
                return Yup.date()
                  .min(minDate, 'Start ride series date is not a valid date')
                  .max(maxDate, 'Start ride series date must be before end ride series date')
                  .nullable()
                  .transform((curr, orig) => (orig === '' ? null : curr))
                  .required('Start Date is Required')
                  .typeError('Start ride series date is not a valid date');
              }

              return Yup.date()
                .min(minDate, 'Start ride series date is not a valid date')
                .nullable()
                .transform((curr, orig) => (orig === '' ? null : curr))
                .required('Start Date is Required')
                .typeError('Start ride series date is not a valid date');
            });
        }

        return Yup.mixed().notRequired();
      }),
      endDate: Yup.lazy(() => {
        if (isSeries) {
          return Yup.mixed()
            .required('End Date is Required')
            .when('startDate', (startDate) => {
              const today = currentDate;
              const startDateIsAfterToday = isAfter(startDate, today);
              const minDate = addDays(startDate, 1);
              const startDateForMaxDate = startDateIsAfterToday ? startDate : today;
              // Need to add 1 day to max to encompass start date
              // gets endOfDay for using the last minute time of day as maxDate
              const maxDate = endOfDay(addDays(addWeeks(startDateForMaxDate, MAXIMUM_SERIES_LENGTH_IN_WEEKS), 1));

              const maxLengthDisplayDate = formatDistanceStrict(today, addWeeks(today, 52.14), { unit: 'month' });

              if (isValid(startDate)) {
                return Yup.date()
                  .min(minDate, 'End ride series date must be after start ride series date')
                  .max(
                    maxDate,
                    `Ride series cannot be longer than ${maxLengthDisplayDate} from ${
                      startDateIsAfterToday ? 'its start date' : 'today'
                    }`
                  )
                  .nullable()
                  .transform((curr, orig) => (orig === '' ? null : curr))
                  .required('End Date is Required')
                  .typeError('End ride series date is not a valid date');
              }

              return Yup.date()
                .min(addDays(nextAvailableDate, 1), 'End ride series date is not a valid date')
                .nullable()
                .transform((curr, orig) => (orig === '' ? null : curr))
                .required('End Date is Required')
                .typeError('End ride series date is not a valid date');
            });
        }

        return Yup.mixed().notRequired();
      }),
    }),
  });
}
